import React, { useState, useEffect } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

interface CustomField {
  id: number
  entity_type: 'lead' | 'deal'
  name: string
  label: string
  field_type: string
  field_type_display: string
  options: string[]
  is_required: boolean
  default_value: string
  description: string
  order: number
  is_active: boolean
}

type EntityTab = 'lead' | 'deal'

const CustomFields = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<EntityTab>('lead')
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingField, setEditingField] = useState<CustomField | null>(null)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    name: '',
    label: '',
    field_type: 'text',
    options: '',
    is_required: false,
    default_value: '',
    description: '',
    order: 0,
  })

  const fieldTypes = [
    { value: 'text', label: 'Texto' },
    { value: 'number', label: 'Número' },
    { value: 'date', label: 'Data' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Telefone' },
    { value: 'select', label: 'Seleção (Dropdown)' },
    { value: 'multiselect', label: 'Múltipla Seleção' },
    { value: 'checkbox', label: 'Checkbox' },
  ]

  useEffect(() => {
    loadCustomFields()
  }, [activeTab])

  const loadCustomFields = async () => {
    try {
      setIsLoading(true)
      const response = await api.get(`/leads/custom-fields/?entity_type=${activeTab}`)
      setCustomFields(Array.isArray(response.data) ? response.data : (response.data.results || []))
    } catch (err: any) {
      console.error('Error loading custom fields:', err)
      setError('Erro ao carregar campos customizados')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const payload: any = {
        entity_type: activeTab,
        name: formData.name,
        label: formData.label,
        field_type: formData.field_type,
        is_required: formData.is_required,
        description: formData.description,
        order: formData.order,
      }

      if (formData.field_type === 'select' || formData.field_type === 'multiselect') {
        payload.options = formData.options.split(',').map(o => o.trim()).filter(o => o)
      }

      if (formData.default_value) {
        payload.default_value = formData.default_value
      }

      if (editingField) {
        await api.put(`/leads/custom-fields/${editingField.id}/`, payload)
      } else {
        await api.post('/leads/custom-fields/', payload)
      }

      setIsModalOpen(false)
      resetForm()
      loadCustomFields()
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.message || 'Erro ao salvar campo')
    }
  }

  const handleEdit = (field: CustomField) => {
    setEditingField(field)
    setFormData({
      name: field.name,
      label: field.label,
      field_type: field.field_type,
      options: Array.isArray(field.options) ? field.options.join(', ') : '',
      is_required: field.is_required,
      default_value: field.default_value || '',
      description: field.description || '',
      order: field.order,
    })
    setIsModalOpen(true)
  }

  const handleDelete = async (fieldId: number, fieldName: string) => {
    if (!window.confirm(`Tem certeza que deseja deletar o campo "${fieldName}"?`)) return

    try {
      await api.delete(`/leads/custom-fields/${fieldId}/`)
      loadCustomFields()
    } catch (err: any) {
      console.error('Error deleting field:', err)
      alert('Erro ao deletar campo')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      label: '',
      field_type: 'text',
      options: '',
      is_required: false,
      default_value: '',
      description: '',
      order: customFields.length,
    })
    setEditingField(null)
    setError('')
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    resetForm()
  }

  const getFieldTypeBadge = (type: string) => {
    const colors: any = {
      text: '#3b82f6',
      number: '#10b981',
      date: '#8b5cf6',
      email: '#f59e0b',
      phone: '#ec4899',
      select: '#6366f1',
      multiselect: '#8b5cf6',
      checkbox: '#06b6d4',
    }
    return {
      backgroundColor: colors[type] || '#6b7280',
      color: 'white',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
    }
  }

  const tabLabel = activeTab === 'lead' ? 'leads' : 'negociações'

  return (
    <div style={{ padding: 'var(--space-6)' }}>
      {/* Back to Settings */}
      <button
        onClick={() => navigate('/settings')}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary)', fontSize: 'var(--text-sm)',
          padding: 'var(--space-1) 0', marginBottom: 'var(--space-4)',
          transition: 'color 0.2s'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--primary-600)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
      >
        <ArrowLeft size={16} />
        Voltar para Configurações
      </button>

      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'bold', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
            Campos Customizados
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
            Crie e gerencie campos personalizados para seus {tabLabel}
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setIsModalOpen(true) }}
          style={{
            background: 'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))',
            color: 'white',
            padding: 'var(--space-3) var(--space-5)',
            borderRadius: 'var(--radius-lg)',
            fontSize: 'var(--text-sm)',
            fontWeight: '500',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            boxShadow: 'var(--shadow-lg)'
          }}
        >
          <span>+</span> Novo Campo
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: 'var(--space-6)',
        background: 'var(--glass-bg)',
        backdropFilter: 'var(--glass-blur)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-xl)',
        padding: '4px',
        width: 'fit-content',
        boxShadow: 'var(--shadow-glass)',
      }}>
        {([
          { key: 'lead', icon: '👤', label: 'Leads' },
          { key: 'deal', icon: '💼', label: 'Negociações' },
        ] as { key: EntityTab; icon: string; label: string }[]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '8px 20px',
              borderRadius: 'var(--radius-lg)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              background: activeTab === tab.key
                ? 'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))'
                : 'transparent',
              color: activeTab === tab.key ? 'white' : 'var(--text-secondary)',
              boxShadow: activeTab === tab.key ? 'var(--shadow-md)' : 'none',
            }}
          >
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#dc2626',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '16px',
        }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div style={{ padding: 'var(--space-12)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{
            width: '48px', height: '48px',
            border: '3px solid var(--glass-border)',
            borderTopColor: 'var(--color-primary-600)',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
            marginBottom: 'var(--space-4)'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: 'var(--text-secondary)' }}>Carregando campos...</p>
        </div>
      ) : customFields.length === 0 ? (
        <div style={{
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          padding: 'var(--space-12)',
          borderRadius: 'var(--radius-2xl)',
          textAlign: 'center',
          border: '2px dashed var(--glass-border)',
          boxShadow: 'var(--shadow-glass)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: 'var(--space-4)' }}>📝</div>
          <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-2)', fontWeight: 'bold', color: 'var(--text-primary)' }}>
            Nenhum campo customizado para {tabLabel}
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
            Crie campos personalizados para enriquecer suas {tabLabel}
          </p>
          <button
            onClick={() => { resetForm(); setIsModalOpen(true) }}
            style={{
              background: 'linear-gradient(135deg, var(--color-primary-600), var(--color-primary-700))',
              color: 'white',
              padding: 'var(--space-3) var(--space-5)',
              borderRadius: 'var(--radius-lg)',
              fontSize: 'var(--text-sm)',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Criar Primeiro Campo
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {customFields.map((field) => (
            <div
              key={field.id}
              style={{
                background: 'var(--glass-bg-strong)',
                backdropFilter: 'var(--glass-blur)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-5)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                boxShadow: 'var(--shadow-glass)',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                    {field.label}
                  </h3>
                  <span style={getFieldTypeBadge(field.field_type)}>
                    {field.field_type_display}
                  </span>
                  {field.is_required && (
                    <span style={{
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                    }}>
                      Obrigatório
                    </span>
                  )}
                </div>
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '12px' }}>
                  Nome interno: <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>
                    {field.name}
                  </code>
                </p>
                {field.description && (
                  <p style={{ color: '#4b5563', fontSize: '14px', marginBottom: '12px' }}>
                    {field.description}
                  </p>
                )}
                {field.options && field.options.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', marginBottom: '4px' }}>
                      Opções:
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {field.options.map((option, index) => (
                        <span key={index} style={{
                          backgroundColor: '#f3f4f6',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                        }}>
                          {option}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {field.default_value && (
                  <div style={{ fontSize: '14px', color: '#4b5563' }}>
                    Valor padrão: <strong>{field.default_value}</strong>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                <button
                  onClick={() => handleEdit(field)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(field.id, field.label)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  Deletar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur)',
            WebkitBackdropFilter: 'var(--glass-blur)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-2xl)',
            padding: 'var(--space-6)',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: 'var(--shadow-xl)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                {editingField ? 'Editar Campo' : `Novo Campo — ${activeTab === 'lead' ? 'Lead' : 'Negociação'}`}
              </h2>
              <button
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                    Nome Interno *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Ex: valor_entrada"
                    style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Usado internamente (sem espaços ou caracteres especiais)
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                    Label para Exibição *
                  </label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    required
                    placeholder="Ex: Valor de Entrada"
                    style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                    Tipo de Campo *
                  </label>
                  <select
                    value={formData.field_type}
                    onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
                    required
                    style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  >
                    {fieldTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {(formData.field_type === 'select' || formData.field_type === 'multiselect') && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                      Opções *
                    </label>
                    <input
                      type="text"
                      value={formData.options}
                      onChange={(e) => setFormData({ ...formData, options: e.target.value })}
                      required
                      placeholder="Opção 1, Opção 2, Opção 3"
                      style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                    />
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Separe as opções com vírgula
                    </p>
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                    Valor Padrão
                  </label>
                  <input
                    type="text"
                    value={formData.default_value}
                    onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
                    placeholder="Valor padrão (opcional)"
                    style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                    Descrição
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Descrição do campo (opcional)"
                    style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px', resize: 'vertical' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_required}
                      onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                    />
                    Campo Obrigatório
                  </label>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                    Ordem de Exibição
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    min={0}
                    style={{ width: '100px', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                >
                  {editingField ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomFields
